import SEO from '../components/SEO';

// Static content lists — hoisted to module scope so they don't allocate on every render.
const ELIGIBILITY = [
  'The information you provide is accurate.',
  'You have the legal authority to create an account.',
  'You will comply with all applicable laws and regulations.'
];
const ACCOUNT_RESPONSIBILITIES = [
  'Maintaining the confidentiality of your account credentials.',
  'Restricting access to your account.',
  'All activities that occur under your account.'
];
const PROHIBITED_CONDUCT = [
  'Provide false or misleading information.',
  'Impersonate another person or organization.',
  'Use the platform for unlawful activities.',
  'Attempt to gain unauthorized access to the platform.',
  'Interfere with the security or functionality of the platform.',
  'Submit harmful, abusive, defamatory, or fraudulent content.'
];
const TOURNAMENT_DISCLAIMERS = [
  'Accuracy of tournament information', 'Quality of tournament organization',
  'Availability of tournaments', 'Tournament outcomes', 'Prize distribution', 'Event safety'
];
const EXTERNAL_FORMS_NOTICES = [
  'Beyond Ball India is not responsible for those third-party platforms.',
  'Users are responsible for reviewing any applicable terms or privacy policies.',
  'Beyond Ball India cannot guarantee the functionality, security, or accuracy of third-party services.'
];
const ORGANIZER_RESPONSIBILITIES = [
  'Tournament management', 'Registration handling', 'Event communication',
  'Venue arrangements', 'Rule enforcement', 'Prize distribution',
  'Participant safety', 'Compliance with applicable laws'
];
const IP_RESTRICTIONS = ['Copy', 'Reproduce', 'Distribute', 'Modify', 'Commercially exploit'];
const AVAILABILITY_DISCLAIMERS = ['Continuous availability', 'Error-free operation', 'Uninterrupted access'];
const PLATFORM_RIGHTS = ['Suspend accounts', 'Restrict access', 'Remove content', 'Terminate accounts'];
const LIABILITY_EXCLUSIONS = [
  'Tournament cancellations', 'Organizer disputes', 'Participant disputes',
  'Injuries occurring during events', 'Loss of prizes', 'Travel expenses',
  'Event-related damages', 'Loss of data', 'Business losses',
  'Indirect or consequential damages'
];
const INDEMNIFY_SOURCES = ['Use of the platform', 'Tournament participation', 'Violation of these Terms', 'Violation of applicable laws'];

const Section = ({ n, title, children }) => (
  <section className="mt-10">
    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 tracking-tight">
      <span className="text-[#F97316] mr-2">{n}.</span>{title}
    </h2>
    <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc list-outside pl-5 space-y-1.5 text-gray-700">
    {items.map((it) => <li key={it}>{it}</li>)}
  </ul>
);

const Terms = () => (
  <>
    <SEO
      title="Terms & Conditions"
      description="Terms of service for Beyond Ball India — rules for using our basketball tournament discovery and registration platform."
      path="/terms"
    />

    <div className="pt-24 pb-16 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs font-semibold text-[#F97316] uppercase tracking-wider mb-2">Legal</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-3">Terms & Conditions</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: June 4, 2026</p>

        <div className="space-y-4 text-gray-700 leading-relaxed">
          <p>Welcome to Beyond Ball India ("Beyond Ball India", "we", "our", or "us"). By accessing or using beyondballindia.com, you agree to comply with and be bound by these Terms & Conditions.</p>
          <p className="font-semibold text-gray-900">If you do not agree with these Terms, please do not use the platform.</p>
        </div>

        <Section n="1" title="About Beyond Ball India">
          <p>Beyond Ball India is a platform that helps users discover basketball tournaments, connect with organizers, and access basketball-related opportunities.</p>
          <p>Beyond Ball India acts as a platform provider and does not organize, operate, supervise, or manage tournaments unless explicitly stated otherwise.</p>
        </Section>

        <Section n="2" title="Eligibility">
          <p>By using the platform, you represent that:</p>
          <Bullets items={ELIGIBILITY}/>
          <p>If you are under the age of 18, you should use the platform with the consent of a parent or legal guardian.</p>
        </Section>

        <Section n="3" title="User accounts">
          <p>Users may create accounts as Players or Organizers.</p>
          <p>You are responsible for:</p>
          <Bullets items={ACCOUNT_RESPONSIBILITIES}/>
          <p>You must immediately notify us if you believe your account has been compromised.</p>
        </Section>

        <Section n="4" title="User conduct">
          <p>You agree not to:</p>
          <Bullets items={PROHIBITED_CONDUCT}/>
          <p>We reserve the right to suspend or terminate accounts that violate these rules.</p>
        </Section>

        <Section n="5" title="Tournament listings">
          <p>Tournament information is submitted by organizers.</p>
          <p>While we may review and approve listings before publication, Beyond Ball India does not guarantee:</p>
          <Bullets items={TOURNAMENT_DISCLAIMERS}/>
          <p>Participants are responsible for independently verifying tournament details before registering or participating.</p>
        </Section>

        <Section n="6" title="Registrations and payments">
          <p>All tournament registrations are collected and stored on Beyond Ball India. For paid tournaments, participants pay the organizer directly (via UPI or another method the organizer specifies) and upload a payment screenshot with their registration.</p>
          <p>Beyond Ball India does not process, hold, or refund tournament fees. All payment disputes must be resolved directly between the participant and the organizer.</p>
        </Section>

        <Section n="7" title="Organizer responsibilities">
          <p>Organizers are solely responsible for:</p>
          <Bullets items={ORGANIZER_RESPONSIBILITIES}/>
          <p>Organizers must provide accurate tournament information.</p>
          <p>Beyond Ball India reserves the right to remove tournament listings that contain misleading, fraudulent, or inappropriate information.</p>
        </Section>

        <Section n="8" title="Registration & participation">
          <p>Registering for a tournament through Beyond Ball India does not guarantee participation, approval, selection, or entry into an event.</p>
          <p>Final participation decisions remain the responsibility of tournament organizers.</p>
          <p>Users participate in tournaments at their own discretion and risk.</p>
        </Section>

        <Section n="9" title="Intellectual property">
          <p>All platform content, branding, logos, designs, text, graphics, and software associated with Beyond Ball India are protected by applicable intellectual property laws.</p>
          <p>Users may not:</p>
          <Bullets items={IP_RESTRICTIONS} />
          <p>any content without written permission.</p>
        </Section>

        <Section n="10" title="Platform availability">
          <p>We strive to keep the platform available and functional.</p>
          <p>However, we do not guarantee:</p>
          <Bullets items={AVAILABILITY_DISCLAIMERS} />
          <p>The platform may occasionally be unavailable due to maintenance, updates, or technical issues.</p>
        </Section>

        <Section n="11" title="Account suspension & termination">
          <p>We reserve the right to:</p>
          <Bullets items={PLATFORM_RIGHTS} />
          <p>if users violate these Terms or engage in behavior harmful to the platform or community.</p>
        </Section>

        <Section n="12" title="Limitation of liability">
          <p>To the fullest extent permitted by law, Beyond Ball India shall not be liable for:</p>
          <Bullets items={LIABILITY_EXCLUSIONS}/>
          <p>Participation in any tournament or event is at the user's own risk.</p>
        </Section>

        <Section n="13" title="Indemnification">
          <p>Users agree to indemnify and hold harmless Beyond Ball India, its owners, operators, affiliates, and representatives from any claims, liabilities, damages, losses, or expenses arising from:</p>
          <Bullets items={INDEMNIFY_SOURCES} />
        </Section>

        <Section n="14" title="Privacy">
          <p>Use of the platform is also governed by our Privacy Policy.</p>
          <p>By using the platform, you acknowledge that you have reviewed and understood the Privacy Policy.</p>
        </Section>

        <Section n="15" title="Changes to these terms">
          <p>We may update these Terms & Conditions from time to time.</p>
          <p>Any changes will be posted on this page with an updated revision date.</p>
          <p>Continued use of the platform after changes are posted constitutes acceptance of the updated Terms.</p>
        </Section>

        <Section n="16" title="Contact us">
          <p>If you have any questions regarding these Terms & Conditions, please contact:</p>
          <div className="bg-white border border-gray-200 rounded-2xl p-5 mt-3">
            <p className="font-semibold text-gray-900 mb-1">Beyond Ball India</p>
            <p>Website: <a href="https://beyondballindia.com" className="text-[#F97316] hover:text-orange-700 font-semibold">beyondballindia.com</a></p>
            <p>Email: <a href="mailto:beyondballindia@gmail.com" className="text-[#F97316] hover:text-orange-700 font-semibold">beyondballindia@gmail.com</a></p>
          </div>
        </Section>
      </div>
    </div>
  </>
);

export default Terms;
